import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type { IApiGeneralParams, IApiPaginatedResponse, IApiResponse } from "@/types";
import type {
  ISingleProject,
  IProjectStats,
  ENUM_PROJECT_STATUSES,
  ISingleProjectAttachment,
  IAddProjectKPIPayload,
  ISingleKPI,
  ISingleOKR,
} from "@/types/(company)/projects.interface";
import type { ISingleTeam } from "@/types/(company)/teams.interface";
import { createApi } from "@reduxjs/toolkit/query/react";

export const companyProjectsApi = createApi({
  reducerPath: "companyProjectsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "GetAllProjects",
    "GetProjectStats",
    "GetProjectDetailsById",
    "GetAllProjectAttachements",
    "GetAllProjectKPIs",
    "GetAllProjectOKRs",
    "GetAllProjectTeams",
  ],
  endpoints: (builder) => ({
    getProjectStats: builder.query<IApiPaginatedResponse<IProjectStats>, void>({
      query: () => ({
        url: APIEndpoints.Company.Projects.GetProjectStats,
      }),
      providesTags: ["GetProjectStats"],
    }),
    getAllProjects: builder.query<
      IApiPaginatedResponse<ISingleProject[]>,
      IApiGeneralParams & { branchId?: number; departmentId?: number; startDate?: string; endDate?: string }
    >({
      query: (params) => ({
        url: APIEndpoints.Company.Projects.GetAllProjects,
        params,
      }),
      providesTags: ["GetAllProjects"],
    }),
    getProjectsInfinite: builder.infiniteQuery<
      IApiPaginatedResponse<ISingleProject[]>,
      { limit: number; search?: string; branchId?: number },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (result) => {
          if (!result?.meta) return undefined;
          const { page, totalPages } = result.meta;
          return page < totalPages ? page + 1 : undefined;
        },
      },
      query: ({ queryArg, pageParam }) => ({
        url: APIEndpoints.Company.Projects.GetAllProjects,
        method: "GET",
        params: {
          limit: queryArg.limit,
          page: pageParam,
          search: queryArg.search,
          ...(queryArg.branchId ? { branchId: queryArg.branchId } : {}),
        },
      }),
      providesTags: ["GetAllProjects"],
    }),
    createProject: builder.mutation<IApiResponse<ISingleProject>, FormData>({
      query: (body) => ({
        url: APIEndpoints.Company.Projects.CreateProject,
        method: "POST",
        body,
      }),
      invalidatesTags: ["GetAllProjects"],
    }),
    updateProject: builder.mutation<IApiResponse<void>, { projectId: string; payload: FormData }>({
      query: ({ projectId, payload }) => ({
        url: APIEndpoints.Company.Projects.ProjectDetailsById(projectId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["GetAllProjects"],
    }),
    deleteProject: builder.mutation<IApiResponse<void>, { projectId: string }>({
      query: ({ projectId }) => ({
        url: APIEndpoints.Company.Projects.ProjectDetailsById(projectId),
        method: "DELETE",
      }),
      invalidatesTags: ["GetAllProjects", "GetProjectStats"],
    }),
    patchProjectStatus: builder.mutation<IApiResponse<void>, { projectId: string; status: ENUM_PROJECT_STATUSES }>({
      query: ({ projectId, status }) => ({
        url: APIEndpoints.Company.Projects.PatchProjectStatus(projectId),
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: ["GetProjectDetailsById"],
    }),
    getProjectDetailsById: builder.query<IApiPaginatedResponse<ISingleProject>, { projectId: string }>({
      query: ({ projectId }) => ({
        url: APIEndpoints.Company.Projects.ProjectDetailsById(projectId),
      }),
      providesTags: ["GetProjectDetailsById"],
    }),
    // PROJECT TEAMS
    getProjectTeamsByProjectId: builder.query<
      IApiPaginatedResponse<ISingleTeam[]>,
      { projectId: string; params?: IApiGeneralParams }
    >({
      query: ({ projectId, params }) => ({
        url: APIEndpoints.Company.Projects.Team.GetProjectTeamsByProjectId(projectId),
        params,
      }),
      providesTags: ["GetAllProjectTeams"],
    }),
    assignProjectTeams: builder.mutation<
      IApiPaginatedResponse<ISingleProject>,
      { projectId: string; teamIds: number[] }
    >({
      query: ({ projectId, teamIds }) => ({
        url: APIEndpoints.Company.Projects.Team.AssignProjectTeams(projectId),
        method: "POST",
        body: { teamIds },
      }),
      invalidatesTags: ["GetAllProjectTeams"],
    }),
    // PROJECT ATTACHMENTS
    getProjectAttachmentById: builder.query<
      IApiResponse<ISingleProjectAttachment>,
      { projectId: string; attachmentId: string }
    >({
      query: ({ projectId, attachmentId }) => ({
        url: APIEndpoints.Company.Projects.Attachments.ProjectDocumentEndPointById(projectId, attachmentId),
      }),
      providesTags: ["GetAllProjectAttachements"],
    }),
    getAllProjectAttachments: builder.query<
      IApiPaginatedResponse<ISingleProjectAttachment[]>,
      { projectId: string; params?: IApiGeneralParams }
    >({
      query: ({ projectId, params }) => ({
        url: APIEndpoints.Company.Projects.Attachments.GetProjectAttachments(projectId),
        params,
      }),
      providesTags: ["GetAllProjectAttachements"],
    }),
    uploadProjectAttachment: builder.mutation<IApiResponse<void>, { projectId: string; payload: FormData }>({
      query: ({ projectId, payload }) => ({
        url: APIEndpoints.Company.Projects.Attachments.UploadProjectAttachment(projectId),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["GetAllProjectAttachements"],
    }),
    deleteProjectAttachment: builder.mutation<IApiResponse<void>, { projectId: string; attachmentId: string }>({
      query: ({ projectId, attachmentId }) => ({
        url: APIEndpoints.Company.Projects.Attachments.ProjectDocumentEndPointById(projectId, attachmentId),
        method: "DELETE",
      }),
      invalidatesTags: ["GetAllProjectAttachements"],
    }),
    renameAttachment: builder.mutation<
      IApiResponse<void>,
      { projectId: string; attachmentId: string; payload: { name: string } }
    >({
      query: ({ projectId, attachmentId, payload }) => ({
        url: APIEndpoints.Company.Projects.Attachments.ProjectDocumentEndPointById(projectId, attachmentId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["GetAllProjectAttachements"],
    }),
    // PROJECT KPIs
    getAllProjectKPIs: builder.query<IApiResponse<ISingleKPI[]>, { projectId: string; params: IApiGeneralParams }>({
      query: ({ projectId, params }) => ({
        url: APIEndpoints.Company.Projects.KPI.ProjectKPIsEndpoint(projectId),
        params,
      }),
      providesTags: ["GetAllProjectKPIs"],
    }),
    createProjectKPI: builder.mutation<IApiResponse<void>, { projectId: string; payload: IAddProjectKPIPayload }>({
      query: ({ projectId, payload }) => ({
        url: APIEndpoints.Company.Projects.KPI.ProjectKPIsEndpoint(projectId),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["GetAllProjectKPIs"],
    }),
    updateProjectKPI: builder.mutation<
      IApiResponse<void>,
      { projectId: string; kpiId: string; payload: IAddProjectKPIPayload }
    >({
      query: ({ projectId, kpiId, payload }) => ({
        url: APIEndpoints.Company.Projects.KPI.KPIIdEndpoint(projectId, kpiId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["GetAllProjectKPIs"],
    }),
    getProjectKPIById: builder.query<IApiResponse<ISingleKPI>, { projectId: string; kpiId: string }>({
      query: ({ projectId, kpiId }) => ({
        url: APIEndpoints.Company.Projects.KPI.KPIIdEndpoint(projectId, kpiId),
      }),
    }),
    deleteProjectKPI: builder.mutation<IApiResponse<void>, { projectId: string; kpiId: string }>({
      query: ({ projectId, kpiId }) => ({
        url: APIEndpoints.Company.Projects.KPI.KPIIdEndpoint(projectId, kpiId),
        method: "DELETE",
      }),
      invalidatesTags: ["GetAllProjectKPIs"],
    }),
    // PROJECT OKRs
    getAllProjectOKRs: builder.query<IApiResponse<ISingleOKR[]>, { projectId: string; params: IApiGeneralParams }>({
      query: ({ projectId, params }) => ({
        url: APIEndpoints.Company.Projects.OKR.ProjectOKRsEndpoint(projectId),
        params,
      }),
      providesTags: ["GetAllProjectOKRs"],
    }),
    getProjectOKRById: builder.query<IApiResponse<ISingleKPI>, { projectId: string; okrId: string }>({
      query: ({ projectId, okrId }) => ({
        url: APIEndpoints.Company.Projects.OKR.OKRIdEndpoint(projectId, okrId),
      }),
    }),
    createProjectOKR: builder.mutation<IApiResponse<void>, { projectId: string; payload: IAddProjectKPIPayload }>({
      query: ({ projectId, payload }) => ({
        url: APIEndpoints.Company.Projects.OKR.ProjectOKRsEndpoint(projectId),
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["GetAllProjectOKRs"],
    }),
    updateProjectOKR: builder.mutation<
      IApiResponse<void>,
      { projectId: string; okrId: string; payload: IAddProjectKPIPayload }
    >({
      query: ({ projectId, okrId, payload }) => ({
        url: APIEndpoints.Company.Projects.OKR.OKRIdEndpoint(projectId, okrId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["GetAllProjectOKRs"],
    }),
    deleteProjectOKR: builder.mutation<IApiResponse<void>, { projectId: string; okrId: string }>({
      query: ({ projectId, okrId }) => ({
        url: APIEndpoints.Company.Projects.OKR.OKRIdEndpoint(projectId, okrId),
        method: "DELETE",
      }),
      invalidatesTags: ["GetAllProjectOKRs"],
    }),
  }),
});

// PROJECTS
export const {
  useGetAllProjectsQuery,
  useGetProjectStatsQuery,
  useUpdateProjectMutation,
  useCreateProjectMutation,
  useGetProjectDetailsByIdQuery,
  usePatchProjectStatusMutation,
  useDeleteProjectMutation,
  useGetProjectsInfiniteInfiniteQuery,
} = companyProjectsApi;

// PROJECT TEAMS
export const { useAssignProjectTeamsMutation, useGetProjectTeamsByProjectIdQuery } = companyProjectsApi;

// PROJECT ATTACHMENTS
export const {
  useGetProjectAttachmentByIdQuery,
  useGetAllProjectAttachmentsQuery,
  useUploadProjectAttachmentMutation,
  useDeleteProjectAttachmentMutation,
  useRenameAttachmentMutation,
} = companyProjectsApi;

// PROJECT KPI
export const {
  useGetAllProjectKPIsQuery,
  useCreateProjectKPIMutation,
  useUpdateProjectKPIMutation,
  useGetProjectKPIByIdQuery,
  useDeleteProjectKPIMutation,
} = companyProjectsApi;

// PROJECT OKR
export const {
  useGetAllProjectOKRsQuery,
  useCreateProjectOKRMutation,
  useUpdateProjectOKRMutation,
  useGetProjectOKRByIdQuery,
  useDeleteProjectOKRMutation,
} = companyProjectsApi;
